import { useCallback, useSyncExternalStore } from "react"

import type { VoiceEngine } from "../utils/voice-engine"

import { useFeatureEnabled } from "@/hooks/use-feature-enabled"

import {
  chooseVoiceEngine,
  readVoiceEnginePreference,
  subscribeVoiceEnginePreference,
  writeVoiceEnginePreference,
} from "../utils/voice-engine"

/**
 * Which voice engine this user gets: GPT-Live when `live_voice` is enabled,
 * else GPT Realtime when `realtime_audio` is, else none (no voice button).
 *
 * Built on `useFeatureEnabled` on purpose: every surface and test that already
 * gates voice through that hook (including the suites that mock it to render
 * without a QueryClientProvider) keeps working unchanged. The pure form for
 * non-hook callers is `resolveVoiceEngine`.
 */
export function useVoiceEngine(): VoiceEngine | null {
  return useVoiceEngineChoice().engine
}

export type VoiceEngineChoice = {
  engine: VoiceEngine | null
  /** The engine the person can switch to, when the tenant has both. */
  otherEngine: VoiceEngine | null
  /** Remember the other engine on this device and select it. */
  switchEngine: () => void
}

/**
 * The engine plus the device preference that can override it. Only a tenant
 * with both engines enabled gets a choice; otherwise the flags decide alone.
 */
export function useVoiceEngineChoice(): VoiceEngineChoice {
  const isLiveEnabled = useFeatureEnabled("live_voice")
  const isRealtimeEnabled = useFeatureEnabled("realtime_audio")
  const preference = useSyncExternalStore(
    subscribeVoiceEnginePreference,
    readVoiceEnginePreference,
    () => "auto" as const
  )
  const enabled: VoiceEngine[] = []
  if (isLiveEnabled) enabled.push("live")
  if (isRealtimeEnabled) enabled.push("realtime")
  const engine = chooseVoiceEngine(enabled, preference)
  const otherEngine = engine ? (enabled.find((candidate) => candidate !== engine) ?? null) : null
  const switchEngine = useCallback(() => {
    if (otherEngine) writeVoiceEnginePreference(otherEngine)
  }, [otherEngine])
  return { engine, otherEngine, switchEngine }
}
